import { stringify as stringifyQuery } from 'qs';
import { prependCountryAndLocale } from './prependCountryAndLocale';
import { prependLocale } from './prependLocale';
import { translate } from './translate';
import type { Param, PathParams, QueryParams } from './types';
import { currentSite } from 'site/currentSite';

type FormatOptions = {
  translate: boolean;
  prependLocale: boolean;
};

export const EMPTY_PARAM = 'EMPTY_PARAM';

export function format(
  template: string,
  pathParams: PathParams,
  queryParams: QueryParams,
  options: FormatOptions
): string {
  const site = currentSite();
  const parameters = parametersOf(template);
  let result = template;

  if (options.translate) {
    result = translate(result);
  }
  if (options.prependLocale) {
    result = prependLocale(result, site);
  }
  result = prependCountryAndLocale(result, site);
  result = interpolatePath(result, pathParams, parameters);
  result = appendQuery(result, queryParams);

  return result;
}

// A path parameter is `:name`, or `*name` for one that spans segments. An
// optional one sits in parentheses with the text before it, `(/:name)`, so that
// `EMPTY_PARAM` can drop the group whole.
const OPTIONAL_PARAMETER = /\(([^():*]*[:*]([a-z0-9_]+))\)/gi;
const PARAMETER = /([:*])([a-z0-9_]+)/gi;

// Names are read from the template before it is translated or prefixed: a
// route translation is text, and whatever it holds stays as it is.
function parametersOf(template: string): ReadonlySet<string> {
  return new Set(
    (template.match(PARAMETER) ?? []).map(parameter =>
      parameter.slice(1).toLowerCase()
    )
  );
}

// The template is read for the parameters it names, and each is looked up; the
// keys of `pathParams` are not walked. A generated helper's parameter type is
// not exact, so a wider object type-checks, and a key the template does not name
// is ignored rather than refused. Values go in only after the optional groups
// are settled, all in one pass, so none is read back as template: a slug of
// `a:id` stays that text beside an `:id`.
function interpolatePath(
  path: string,
  pathParams: PathParams,
  parameters: ReadonlySet<string>
): string {
  const values = new Map<string, unknown>(
    Object.entries(pathParams).map(([name, value]) => [
      name.toLowerCase(),
      value
    ])
  );
  const valueOf = (name: string): unknown => values.get(name.toLowerCase());
  const isParameter = (name: string): boolean =>
    parameters.has(name.toLowerCase());

  return path
    .replace(
      OPTIONAL_PARAMETER,
      (group: string, parameter: string, name: string) => {
        if (!isParameter(name)) {
          return group;
        }
        return valueOf(name) === EMPTY_PARAM ? '' : parameter;
      }
    )
    .replace(PARAMETER, (text: string, sigil: string, name: string) => {
      if (!isParameter(name)) {
        return text;
      }
      const value = valueOf(name);

      if (value === EMPTY_PARAM) {
        // Which parameters are optional is not in the types, so a required one
        // handed `EMPTY_PARAM` is refused here instead.
        throw new Error(`Path parameter "${name}" is required`);
      }
      if (!isParam(value)) {
        throw new Error(`No value supplied for path parameter :${name}`);
      }
      return sigil === '*'
        ? escapePath(value.toString())
        : escapeSegment(value.toString());
    });
}

// A value is escaped as Rails' own helper escapes it, so a path built here is the
// one the server builds: everything is percent-encoded but the unreserved
// characters, the sub-delimiters, `:` and `@`. `encodeURIComponent` encodes
// `$&+,;=:@` as well, so those are let back through.
function escapeSegment(value: string): string {
  return encodeURIComponent(value).replace(/%(24|26|2B|2C|3A|3B|3D|40)/g, hex =>
    decodeURIComponent(hex)
  );
}

// A `*name` parameter spans segments, so its slashes stay as they are.
function escapePath(value: string): string {
  return escapeSegment(value).replace(/%2F/g, '/');
}

// A path parameter's value is non-empty text or a number, 0 included. The type
// says as much, but an untyped caller is not bound by it and can pass anything:
// a `false` from `cond && id`, or a jQuery `.data()` attribute read back as a
// boolean. NaN is a number and still no value -- it is what a coercing extractor
// degrades an unusable id to, and refusing it is what stops a request for that
// id leaving the browser.
function isParam(value: unknown): value is Param {
  return typeof value === 'string'
    ? value !== ''
    : typeof value === 'number' && !Number.isNaN(value);
}

function appendQuery(path: string, queryParams: QueryParams): string {
  const query = stringifyQuery(queryParams, {
    arrayFormat: 'brackets',
    skipNulls: true
  });

  if (query) {
    return `${path}?${query}`;
  } else {
    return path;
  }
}
